/**
 * Hook dữ liệu của màn Kho dữ liệu (`/tro-ly-ai/ban-sao`, `/tro-ly-ai/ban-sao/:nguon`). View chỉ gọi hook ở đây.
 *
 * Mọi khoá của một nguồn nằm dưới `KHOA.nguonChiTiet(id)` — sau mỗi lần ghi bản khai thì vô hiệu cả nhánh ấy: trạng thái,
 * bản khai, hồ sơ và sơ đồ ở Kết nối nguồn cùng đọc từ bản khai, bỏ sót một cái là ghi xong mà màn hình không đổi.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { khoTroLy } from '../../../lib/repositories/tro-ly-ai.repository';
import type { DinhNghia, Finding, GlossaryTerm, TraLoiPhongVan } from '../types';
import { ghepDeXuat } from '../nghiep-vu/de-xuat';
import { KHOA, thuLai } from './useChung';

/** Hỏi lại trạng thái trong lúc có chu kỳ đồng bộ đang chạy. */
export const NHIP_THEO_DOI_MS = 5_000;
/** Khoảng chờ ngay sau khi bấm: task nền có thể chưa kịp ghi tiến độ đầu tiên, dừng hỏi ở đó là bỏ lỡ cả chu kỳ. */
export const CHO_BAT_DAU_MS = 30_000;

export const KHOA_KHO = {
  bangDaKhai: (id: string) => [...KHOA.nguonChiTiet(id), 'bang'] as const,
  hoSo: (id: string) => [...KHOA.nguonChiTiet(id), 'ho-so'] as const,
  thuatNgu: (id: string) => [...KHOA.nguonChiTiet(id), 'thuat-ngu'] as const,
  dinhNghia: (id: string) => [...KHOA.nguonChiTiet(id), 'dinh-nghia'] as const,
  nguCanh: (id: string) => [...KHOA.nguonChiTiet(id), 'ngu-canh'] as const,
};

function useLamMoiNguon() {
  const qc = useQueryClient();
  return (id: string) => qc.invalidateQueries({ queryKey: KHOA.nguonChiTiet(id) });
}

/**
 * Trạng thái bản sao của một nguồn. `theoDoiDen` > bây giờ thì hỏi lại đều đặn; ngoài ra chỉ hỏi lại khi có chu kỳ đang
 * chạy — kể cả chu kỳ do người khác bấm. Xong là thôi.
 */
export function useTrangThaiNguon(id: string, theoDoiDen = 0) {
  return useQuery({
    queryKey: KHOA.trangThai(id),
    queryFn: () => khoTroLy.trangThai(id),
    enabled: Boolean(id),
    staleTime: 30_000,
    retry: thuLai,
    refetchInterval: (q) => (q.state.data?.dongBo?.dangChay || Date.now() < theoDoiDen ? NHIP_THEO_DOI_MS : false),
  });
}

/** Trạng thái của nhiều nguồn một lượt — chân thẻ ở lưới nguồn. Cùng khoá với màn chi tiết nên bấm thẻ là có sẵn. */
export function useTrangThaiNhieuNguon(ids: string[]) {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: KHOA.trangThai(id),
      queryFn: () => khoTroLy.trangThai(id),
      staleTime: 30_000,
      retry: thuLai,
    })),
  });
}

export function useBangDaKhai(id: string) {
  return useQuery({
    queryKey: KHOA_KHO.bangDaKhai(id),
    queryFn: () => khoTroLy.bangDaKhai(id),
    enabled: Boolean(id),
    staleTime: 30_000,
    retry: thuLai,
  });
}

/** Hồ sơ thống kê đã đo — số dòng của nguồn trực tiếp, cột rỗng ở hộp chọn bảng. */
export function useHoSoNguon(id: string, bat = true) {
  return useQuery({
    queryKey: KHOA_KHO.hoSo(id),
    queryFn: () => khoTroLy.hoSo(id),
    enabled: Boolean(id) && bat,
    staleTime: 60_000,
    retry: thuLai,
  });
}

export function useThuatNgu(id: string) {
  return useQuery({
    queryKey: KHOA_KHO.thuatNgu(id),
    queryFn: () => khoTroLy.thuatNgu(id),
    enabled: Boolean(id),
    staleTime: 60_000,
    retry: thuLai,
  });
}

export function useDinhNghia(id: string) {
  return useQuery({
    queryKey: KHOA_KHO.dinhNghia(id),
    queryFn: () => khoTroLy.dinhNghia(id),
    enabled: Boolean(id),
    staleTime: 60_000,
    retry: thuLai,
  });
}

/** Gói ngữ cảnh — ở đây chỉ để biết nguồn ĐANG gắn bộ tri thức nào. */
export function useNguCanhNguon(id: string) {
  return useQuery({
    queryKey: KHOA_KHO.nguCanh(id),
    queryFn: () => khoTroLy.goiNguCanh(id),
    enabled: Boolean(id),
    staleTime: 30_000,
    retry: thuLai,
  });
}

/** Đồng bộ cả nguồn (`tables` trống) hoặc lẻ vài bảng. Máy chủ chỉ BẮT ĐẦU chu kỳ rồi trả về. */
export function useDongBo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tables?: string[]) => khoTroLy.dongBo(id, tables),
    onSuccess: () => qc.invalidateQueries({ queryKey: KHOA.trangThai(id) }),
  });
}

/** Đọc LƯỢC ĐỒ nguồn để mở hộp chọn bảng — chỉ cần kết nối, chưa cần đồng bộ. */
export function useDocLuocDo(id: string) {
  return useMutation({ mutationFn: () => khoTroLy.doNguon(id) });
}

/** Đo thống kê cột (nguồn trực tiếp). Số đo mới đổi câu hỏi phỏng vấn và gợi ý của mọi tab thuộc nguồn này. */
export function useDoThongKe(id: string) {
  const lamMoi = useLamMoiNguon();
  return useMutation({ mutationFn: () => khoTroLy.chayHoSo(id), onSuccess: () => lamMoi(id) });
}

/** Dò mã trạng thái, quan hệ ngầm, bảng chết trên kho đã có dữ liệu. */
export function useDoTim(id: string) {
  return useMutation({ mutationFn: () => khoTroLy.doTim(id) });
}

/** Ghép một đề xuất vào bản khai: đọc bản khai hiện tại, vá đúng chỗ (`ghepDeXuat`), ghi lại cả mảng. */
export function useGhiDeXuat(id: string) {
  const lamMoi = useLamMoiNguon();
  return useMutation({
    mutationFn: async (f: Finding) => {
      const tables = await khoTroLy.bangDaKhai(id);
      await khoTroLy.luuBang(id, ghepDeXuat(tables, f));
      return f;
    },
    onSuccess: () => lamMoi(id),
  });
}

/** Gỡ MỘT bảng khỏi bản khai và xoá luôn ở kho bản sao — không lùi được. */
export function useXoaBang(id: string) {
  const lamMoi = useLamMoiNguon();
  return useMutation({ mutationFn: (bang: string) => khoTroLy.xoaBang(id, bang), onSuccess: () => lamMoi(id) });
}

/** Lưu bản khai theo gói 100 bảng, báo tiến độ. `giuLoiKhai` bật khi gửi ĐỀ XUẤT dò được (giữ lời khai cũ). */
export function useLuuBang(id: string) {
  const lamMoi = useLamMoiNguon();
  return useMutation({
    mutationFn: (v: {
      tables: Record<string, unknown>[];
      giuLoiKhai?: boolean;
      onTien?: (xong: number, tong: number) => void;
    }) => khoTroLy.luuBangTheoGoi(id, v.tables, v.giuLoiKhai ?? false, v.onTien),
    onSuccess: () => lamMoi(id),
  });
}

/** Ghi chú nghiệp vụ cho cả nguồn — một trường của nguồn, nên làm mới cả danh sách nguồn. */
export function useLuuGhiChu(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ghiChuNghiepVu: string) => khoTroLy.suaNguon(id, { ghiChuNghiepVu }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KHOA.nguon }),
  });
}

/** THAY TRỌN danh sách thuật ngữ. */
export function useLuuThuatNgu(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ds: GlossaryTerm[]) => khoTroLy.luuThuatNgu(id, ds),
    onSuccess: (r) => qc.setQueryData(KHOA_KHO.thuatNgu(id), r),
  });
}

/** THAY TRỌN danh sách định nghĩa. */
export function useLuuDinhNghia(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ds: DinhNghia[]) => khoTroLy.luuDinhNghia(id, ds),
    onSuccess: (r) => qc.setQueryData(KHOA_KHO.dinhNghia(id), r),
  });
}

export function useGanTriThuc(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => khoTroLy.ganTriThuc(id, ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: KHOA_KHO.nguCanh(id) }),
  });
}

/**
 * Lấy câu phỏng vấn của một bảng (hoặc tất cả với `tatCa`). Là mutation chứ không `useQuery`: màn hình tự giữ kho câu
 * của những bảng đã mở, và gọi lại máy chủ khi đang gõ dở là ném mất phần người dùng vừa gõ.
 */
export function usePhongVan(id: string) {
  return useMutation({
    mutationFn: (v: { bang?: string | null; hauQua?: string | null; tatCa?: boolean }) => khoTroLy.phongVan(id, v),
  });
}

export function usePhamViHangLoat(id: string) {
  const lamMoi = useLamMoiNguon();
  return useMutation({
    mutationFn: (giaTri: 'co' | 'khong') => khoTroLy.phamViHangLoat(id, giaTri),
    onSuccess: () => lamMoi(id),
  });
}

export function useGhiPhongVan(id: string) {
  const lamMoi = useLamMoiNguon();
  return useMutation({
    mutationFn: (traLoi: TraLoiPhongVan[]) => khoTroLy.ghiPhongVan(id, traLoi),
    onSuccess: () => lamMoi(id),
  });
}
